import { useEffect } from 'react'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useScript } from 'usehooks-ts'


import { OrderAmount } from '@entities/Order'

import {
  useAuth,
  useLoginModal,
  useProfileCustomerModal,
} from '@features/auth'
import { useCartStore } from '@features/cart/model/useCartStore'
import { useErrorInfoModal } from '@features/modal'
import useModalStore from '@features/modal/model'
import { DeliveryForm, PickupForm, UserInfoForm } from '@features/order-form'
import { OrderFormValues } from '@features/order-form/model/shema'
import { useOrderForm } from '@features/order-form/model/useOrderForm.ts'
import DetailOrderForm from '@features/order-form/ui/DetailOrderForm/DetailOrderForm'
import { useAddPayment } from '@features/payment/hooks/useAddPayment'
import { useAddressStore } from '@features/shipping-methods'
import { AddressItemType } from '@features/shipping-methods/types'

import { useHeaderStore } from '@widgets/header/model/headerStore'
import useDeliveryModal from '@widgets/modal-delivery/ui/useDeliveryModal'
import { useSlots, useSlotStore } from '@widgets/slots'


import {
  CustomerAddress,
  useInitializeOrderLazyQuery,
  usePlaceOrderMutation,
  useUpdateOrderInfoWithSlotCheckMutation,
} from '@shared/generated'
import Button from '@shared/ui/button'
import Container from '@shared/ui/container'
import { Form } from '@shared/ui/form'
import Wrapper from '@shared/ui/wrapper'
import { formatPhoneNumber } from '@shared/uttils/formatPhoneNumber'
import { splitFullName } from '@shared/uttils/splitFullName'
import { useDebounce } from '@shared/uttils/useDebounce'

import SuccessModal from './modal/SuccessModal'
import WarningModal from './modal/WarningModal'

import styles from './styles.module.scss'

const PAYMENT_SCRIPT: string = import.meta.env.VITE_HALYK_SCRIPT_URL

export default function OrderDelivery () {
  const { setVariant } = useHeaderStore()
  const { user, fetchPingUser, requireRegistration } = useAuth()
  const { openProfileCustomerModal } = useProfileCustomerModal()
  const { openLoginModal } = useLoginModal()
  const { selectedAddress, isPickup, shopId, zoneId } = useAddressStore()
  const navigate = useNavigate()
  const { openDeliveryModal } = useDeliveryModal()
  const scriptStatus = useScript(PAYMENT_SCRIPT)
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()

  const [ loadOrder, { data: { initializeOrder } = {}, loading: orderLoading } ] =
    useInitializeOrderLazyQuery({
      variables: {
        shopId,
      },
      fetchPolicy: 'network-only',
    })

  useEffect(() => {
    async function checkModals () {
      const data = await fetchPingUser()
      const auth = data.data?.pingAuth
      const isRequireRegistration = auth?.requireRegistration

      if (!auth) {
        openLoginModal(() => {
          navigate('/cart')
        })
        return
      }

      if (isRequireRegistration) {
        openProfileCustomerModal(() => {
          navigate('/cart')
        })
        return
      }

      if (!selectedAddress) {
        openDeliveryModal(() => {
          navigate('/cart')
        })
        return
      }
    }

    checkModals()
  }, [])

  useEffect(() => {
    const canLoad = user && !requireRegistration && !initializeOrder && shopId

    if (!canLoad) return

    loadOrder({
      variables: {
        shopId,
      },
      fetchPolicy: 'network-only',
    })
  }, [ user, selectedAddress, initializeOrder, requireRegistration ])

  const methods = useOrderForm() as UseFormReturn<OrderFormValues>
  const { t: o } = useTranslation('order')
  const { t: tc } = useTranslation('common')
  const [ updateOrderInfoWithSlotCheck ] =
    useUpdateOrderInfoWithSlotCheckMutation()
  const { clearCart } = useCartStore()
  const [ placeOrder, { data, loading: orderPlaceLoading } ] =
    usePlaceOrderMutation()
  const { openModal } = useModalStore()
  const { openSlotsModal } = useSlots()
  const watched = useWatch<OrderFormValues>({
    control: methods.control,
  })
  const { selectedSlot } = useSlotStore()
  const { addPayment } = useAddPayment()

  useEffect(() => {
    setVariant('minimal')
    return () => setVariant('default')
  }, [])

  useEffect(() => {
    const placeOrder = data?.placeOrder
    if (!placeOrder) return

    const { error_code, order_id } = placeOrder
    if (typeof order_id !== 'string') return

    if (error_code === 0) {
      openModal(
        'successPaymentOrder',
        <SuccessModal selectSlot={selectedSlot} id={order_id} />,
      )
      clearCart()
    }
    else if ([ 1, 2, 3 ].includes(error_code)) {
      openModal('warningPaymentOrder', <WarningModal />, {
        title: o('error'),
        description: o('error_description'),
        onClose: () => location.reload(),
      })
    }
  }, [ data?.placeOrder ])

  useEffect(() => {
    if (!user) return
    methods.reset({
      name: user.first_name || '',
      phone: formatPhoneNumber(user.phone || ''),
      address: selectedAddress?.address || '',
      replacementOption: initializeOrder?.assembly_method ?? 1,
      pickup: isPickup(),
    })
  }, [ user, initializeOrder, selectedAddress, methods, isPickup ])

  const createOrder = async () => {
    try {
      await placeOrder({
        variables: {
          orderId: initializeOrder?.id as string,
        },
        update: (cache, { data }) => {
          const newOrder = data?.placeOrder
          if (!newOrder) return

          cache.modify({
            fields: {
              allMyOrders (existing = { edges: [] }) {
                return {
                  ...existing,
                  edges: [ newOrder, ...existing.edges ],
                }
              },
            },
          })
        },
      })
    }
    catch (e) {
      console.error('Ошибка GraphQL или сети:', e)
      openErrorInfoModal({
        description: te('create_order'),
      })
    }
  }

  const onSubmit = (selected: string) => {
    if (selected !== 'new-card') {
      createOrder()
    }
    else {
      addPayment(initializeOrder?.id as string, '/profile/online-orders')
    }
  }

  const {
    comment,
    noIntercomCall,
    replacementOption,
    nameOther,
    phoneOther,
    leaveAtDoor,
    commentBuilder,
  } = watched

  const debouncedName = useDebounce(nameOther, 2000)
  const debouncedPhone = useDebounce(phoneOther, 2000)
  const debouncedComment = useDebounce(comment, 2000)
  const debouncedCommentBuilder = useDebounce(commentBuilder, 2000)

  useEffect(() => {
    const { firstName, lastName } = splitFullName(debouncedName || '')
    const isCustomer = (
      addr: AddressItemType | null,
    ): addr is CustomerAddress =>
      !!addr && typeof addr === 'object' && 'shop' in addr

    const geo = {
      latitude:
        selectedAddress?.customerGeoPoint?.latitude ??
        (isCustomer(selectedAddress)
          ? selectedAddress.shop?.shopGeoPoint?.latitude
          : undefined),
      longitude:
        selectedAddress?.customerGeoPoint?.longitude ??
        (isCustomer(selectedAddress)
          ? selectedAddress.shop?.shopGeoPoint?.longitude
          : undefined),
    }

    if (
      geo.latitude &&
      geo.longitude &&
      selectedSlot?.id &&
      initializeOrder?.id
    ) {
      updateOrderInfoWithSlotCheck({
        variables: {
          orderId: initializeOrder.id,
          orderInfo: {
            zone_id: zoneId,
            address: selectedAddress?.address || '',
            address_appartments: selectedAddress?.address_appartments,
            address_intercom: selectedAddress?.address_intercom,
            address_floor: selectedAddress?.address_floor,
            address_porch: selectedAddress?.address_porch,
            comment: debouncedCommentBuilder,
            no_door_call: noIntercomCall,
            door_delivery: leaveAtDoor,
            assembly_method: replacementOption,
            customer_geoposition: {
              latitude:
                selectedAddress?.customerGeoPoint?.latitude ??
                (isCustomer(selectedAddress)
                  ? selectedAddress.shop?.shopGeoPoint?.latitude
                  : undefined),
              longitude:
                selectedAddress?.customerGeoPoint?.longitude ??
                (isCustomer(selectedAddress)
                  ? selectedAddress.shop?.shopGeoPoint?.longitude
                  : undefined),
            },
            slot_id: +selectedSlot?.id,
            customerCourierMessage: debouncedComment,

            recipientInfo: {
              firstName,
              lastName,
              phone: phoneOther,
            },
          },
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    noIntercomCall,
    replacementOption,
    debouncedName,
    debouncedPhone,
    leaveAtDoor,
    debouncedComment,
    debouncedCommentBuilder,
    selectedSlot?.id,
    initializeOrder?.id,
    selectedAddress,
  ])

  return (
    <Container>
      <div className={styles.root}>
        <div className={styles.root__left}>
          <div className={styles.wrapper}>
            <Form {...methods}>
              <form className={styles.root__form}>
                {isPickup() ? <PickupForm /> : <DeliveryForm />}
                <UserInfoForm isPickup={isPickup()} />
                {selectedSlot && (
                  <Wrapper className={styles.banner}>
                    <h3>{o('data_time')}</h3>
                    <div className={styles.time}>
                      <span>
                        {selectedSlot.slotDate} {tc('from')}{' '}
                        {selectedSlot.slotTime}
                      </span>
                      <Button
                        className={styles.time__button}
                        theme="quaternary"
                        onClick={() => {
                          openSlotsModal()
                        }}
                      >
                        {tc('change')}
                      </Button>
                    </div>
                  </Wrapper>
                )}
                <DetailOrderForm />
              </form>
            </Form>

            {isPickup() && (
              <Wrapper className={styles.banner}>
                <h3>{o('banner.title')}</h3>
                <p>{o('banner.description')}</p>
              </Wrapper>
            )}
          </div>
        </div>

        <OrderAmount
          loading={
            orderPlaceLoading || orderLoading || scriptStatus === 'loading'
          }
          onSubmit={onSubmit}
        />
      </div>
    </Container>
  )
}
